import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import { login } from "utils/auth";
import { endPoint } from "utils/config";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import logo from "assets/img/sparkLoginLogo.svg";
import "./superLogin.css";

const LOGIN = gql`
  mutation adminLogin($emailOrPhoneNumber: String!, $password: String!) {
    adminLogin(input: { emailOrPhoneNumber: $emailOrPhoneNumber, password: $password }) {
      type
      token {
        accessToken
        refreshToken
      }
      fullName
      roles {
        permissions {
          name
        }
      }
    }
  }
`;

const codeOf = (error) =>
  error && error.graphQLErrors && error.graphQLErrors.length
    ? error.graphQLErrors[0].extensions && error.graphQLErrors[0].extensions.code
    : null;

const messageOf = (error) =>
  error && error.graphQLErrors && error.graphQLErrors.length
    ? error.graphQLErrors[0].message
    : "Something went wrong. Please try again.";

const SuperLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [formError, setFormError] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const [adminLogin] = useMutation(LOGIN);

  // Landing here means starting a new session; anything left over from a
  // previous one would otherwise be sent with the first request.
  useEffect(() => {
    window.localStorage.removeItem("shopId");
    window.localStorage.removeItem("refreshToken");
    window.localStorage.removeItem("token");
    window.localStorage.removeItem("shopType");
    window.localStorage.removeItem("verificationCode");
    window.localStorage.removeItem("phoneNumber");
    window.localStorage.removeItem("permissions");
  }, []);

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (submitting) return;

    const trimmedEmail = email.trim();
    setEmailError(trimmedEmail ? "" : "Enter your email or phone number");
    setPasswordError(password ? "" : "Enter your password");
    setFormError("");
    if (!trimmedEmail || !password) return;

    setSubmitting(true);
    adminLogin({ variables: { emailOrPhoneNumber: trimmedEmail, password } })
      .then((res) => {
        const admin = res.data.adminLogin;

        // A shop admin credential works against this endpoint, but the
        // shop panel has its own sign-in with the verification and
        // payment-setup steps this one does not run. Sending them there
        // is safer than dropping them into a half-initialised session.
        if (admin.type !== "SUPER-ADMIN") {
          setSubmitting(false);
          setFormError("That account is not a super admin. Use the shop admin sign in instead.");
          return;
        }

        login(admin.token.accessToken.toString(), admin.token.refreshToken.toString());

        const permissions = [];
        (admin.roles || []).forEach((role) => {
          (role.permissions || []).forEach((permission) => permissions.push(permission.name));
        });
        window.localStorage.setItem("permissions", JSON.stringify(permissions));

        window.location.href = endPoint + "/superAdmin/dashboard";
      })
      .catch((error) => {
        setSubmitting(false);
        const code = codeOf(error);
        // 404 is "no such account", 400 is "wrong password" — anything
        // else (a suspended admin, a network failure) used to fail
        // silently with both fields simply staying blank.
        setEmailError(code === "404" ? "We could not find that account" : "");
        setPasswordError(code === "400" ? "Wrong password" : "");
        setFormError(code === "404" || code === "400" ? "" : messageOf(error));
      });
  };

  return (
    <div className="superLoginPage">
      <form className="superLoginCard" onSubmit={onSubmit}>
        <div className="superLoginBrand">
          <img src={logo} alt="Spark" />
          <h1 className="superLoginTitle">Super admin</h1>
          <span className="superLoginSubtitle">Sign in to manage the Spark platform.</span>
        </div>

        {formError && (
          <div className="dsFormMessage error" role="alert">
            <ErrorOutlineIcon />
            <span>{formError}</span>
          </div>
        )}

        <div className="superLoginForm">
          <Field label="Email or phone number" htmlFor="superLoginEmail" error={emailError}>
            <input
              id="superLoginEmail"
              className="dsInput"
              type="text"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Password" htmlFor="superLoginPassword" error={passwordError}>
            <div className="superLoginPasswordWrap">
              <input
                id="superLoginPassword"
                className="dsInput"
                type={visible ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                type="button"
                className="superLoginReveal"
                onClick={() => setVisible((current) => !current)}
                aria-label={visible ? "Hide password" : "Show password"}
              >
                {visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </button>
            </div>
          </Field>

          <Button type="submit" variant="primary" size="lg" block loading={submitting}>
            {submitting ? "Signing in" : "Sign in"}
          </Button>
        </div>

        <div className="superLoginFooter">
          Shop or restaurant owner? <a href={endPoint + "/login"}>Sign in here</a>
        </div>
      </form>
    </div>
  );
};

export default SuperLogin;
